// Echter API-Client für das Backend
import { merkeFehlgeschlagenenRequest } from '../testing/contextBuffer';
import {
  aktiverStandort,
  meldeSimuliertBis,
  SIMULIERT_BIS_HEADER,
  STANDORT_HEADER,
} from '../simulationsdatum';

interface ApiOptions {
  headers?: Record<string, string>;
  params?: Record<string, string>;
}

interface ApiResponse<T = unknown> {
  data?: T;
  error?: string;
  message?: string;
}

// Backend-URL konfigurieren: bevorzugt VITE_API_BASE, sonst aktuelle Origin
const API_BASE_URL = (import.meta as ImportMeta & { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE
  ?? (typeof window !== 'undefined' ? window.location.origin : '');

const AUTH_EXPIRED_EVENT = 'sidigo:auth_expired';
const SESSION_INFO_KEY = 'sidigo-session-expires-at';
const SESSION_EXPLICIT_KEY = 'sidigo-session-explicit';
const TOKEN_STORAGE_KEY = 'sidigo-token-storage'; // 'local' | 'session'
const REFRESH_TOKEN_KEY = 'sidigo-refresh-token';

type JsonRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is JsonRecord => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
);

const getPathWithoutQuery = (url: string): string => {
  const qIndex = url.indexOf('?');
  return qIndex >= 0 ? url.slice(0, qIndex) : url;
};

const normalizeStandortRecord = (row: JsonRecord): JsonRecord => {
  const isActive = typeof row.is_active === 'boolean'
    ? row.is_active
    : (typeof row.aktiv === 'boolean' ? row.aktiv : true);
  const slug = typeof row.slug === 'string'
    ? row.slug
    : (typeof row.code === 'string' ? row.code : '');

  return {
    ...row,
    is_active: isActive,
    aktiv: isActive,
    slug,
  };
};

const normalizePersonRecord = (row: JsonRecord): JsonRecord => {
  const firstName = typeof row.first_name === 'string' ? row.first_name : '';
  const lastName = typeof row.last_name === 'string' ? row.last_name : '';
  const fullName = `${firstName} ${lastName}`.trim();
  // Lernende tragen ihre eigene ID ("{personId}-{index}") und hängen am Konto der
  // Eltern; für sie darf die Personen-ID die eigene nicht überschreiben.
  const isSubprofile = row.isSubprofile === true;
  const personId = isSubprofile ? row.id : (row.person_id ?? row.id);
  const idStr = personId !== undefined && personId !== null ? String(personId) : '';
  const isActive = typeof row.is_active === 'boolean' ? row.is_active : true;
  const roles = Array.isArray(row.roles) ? row.roles.map(String) : [];
  const login = typeof row.login === 'string' ? row.login : '';
  const email = typeof row.email === 'string' ? row.email : '';
  const given = typeof row.name === 'string' ? row.name.trim() : '';

  return {
    ...row,
    id: idStr,
    name: given || fullName || login || email || (idStr ? `Person #${idStr}` : ''),
    firstName,
    lastName,
    wpUserId: isSubprofile ? String(row.parentId ?? '') : idStr,
    status: isActive ? 'active' : 'inactive',
    // Lehrkraft ist, wer ein Lehrer-Profil hat — ein Login ist dafür nicht nötig.
    // Kunde ist, wer die Rolle oder einen Vertrag hat; ein im Office angelegter
    // Datensatz ist zunächst nichts davon (T-0297). Sagt der Server nichts dazu,
    // bleibt es beim bisherigen „Kunde“.
    type: isSubprofile
      ? 'subprofile'
      : row.isTeacher === true || roles.includes('teacher')
        ? 'teacher'
        : row.isCustomer === false
          ? 'none'
          : 'customer',
    joinDate: typeof row.joinDate === 'string' ? row.joinDate : '',
  };
};

/**
 * Zusatzangaben der Personenliste, die neben den Zeilen mitkommen:
 * `total` ist die Gesamtzahl der Treffer (nicht nur der geladenen Seite),
 * `totalAllStatus` die Zahl ohne den Statusfilter, `withoutStandort` die Zahl
 * der Personen, die der Standortfilter verschluckt.
 */
export const personListMeta = (
  list: unknown
): { total: number; totalAllStatus: number; withoutStandort: number } => ({
  total: Number((list as { total?: unknown })?.total) || 0,
  totalAllStatus: Number((list as { totalAllStatus?: unknown })?.totalAllStatus) || 0,
  withoutStandort: Number((list as { withoutStandort?: unknown })?.withoutStandort) || 0,
});

const normalizeSuccessfulData = (url: string, method: string, responseData: unknown): unknown => {
  const path = getPathWithoutQuery(url);
  if (method === 'POST' && path === '/api/personen' && isRecord(responseData)) {
    return normalizePersonRecord(responseData);
  }
  // Nach dem Speichern dieselbe Form wie beim Laden — sonst fehlte der Typ, und
  // die Akte zeigte wieder „Kunde“.
  if (method === 'PATCH' && /^\/api\/personen\/\d+$/.test(path) && isRecord(responseData)) {
    return normalizePersonRecord(responseData);
  }
  if (method !== 'GET') return responseData;
  if (/^\/api\/standorte\/\d+$/.test(path) && isRecord(responseData)) {
    return normalizeStandortRecord(responseData);
  }
  if (/^\/api\/personen\/\d+$/.test(path) && isRecord(responseData)) {
    return normalizePersonRecord(responseData);
  }
  if (!isRecord(responseData) || !Array.isArray(responseData.items)) return responseData;

  if (path === '/api/standorte') {
    return responseData.items
      .filter((item): item is JsonRecord => isRecord(item))
      .map((item) => normalizeStandortRecord(item));
  }

  if (path === '/api/personen') {
    const items = responseData.items
      .filter((item): item is JsonRecord => isRecord(item))
      .map((item) => normalizePersonRecord(item));
    // Die Antwort bleibt eine Liste, damit die bestehenden Aufrufer unverändert
    // bleiben. Wie viele Personen der Standortfilter ausblendet, hängt als Feld
    // daran und wird mit `personListMeta` gelesen.
    return Object.assign(items, {
      total: Number(responseData.total) || items.length,
      totalAllStatus: Number(responseData.totalAllStatus) || 0,
      withoutStandort: Number(responseData.withoutStandort) || 0,
    });
  }

  if (path === '/api/faecher') {
    return responseData.items;
  }

  return responseData;
};

// Token abrufen: bevorzugt localStorage (Remember-Me), sonst sessionStorage
export const getAuthToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  const local = localStorage.getItem('sidigo-token');
  if (local) return local;
  return sessionStorage.getItem('sidigo-token');
};

export const getRefreshToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  const local = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (local) return local;
  return sessionStorage.getItem(REFRESH_TOKEN_KEY);
};

const setAuthToken = (token: string | null) => {
  if (typeof window === 'undefined') return;
  const storagePref = localStorage.getItem(TOKEN_STORAGE_KEY);
  const hasLocal = !!localStorage.getItem('sidigo-token');
  const hasSession = !!sessionStorage.getItem('sidigo-token');

  const setLocal = () => {
    if (token) localStorage.setItem('sidigo-token', token);
    else localStorage.removeItem('sidigo-token');
  };
  const setSession = () => {
    if (token) sessionStorage.setItem('sidigo-token', token);
    else sessionStorage.removeItem('sidigo-token');
  };

  // Prefer explicit storage hint, otherwise update whichever currently holds the token.
  if (storagePref === 'local') return setLocal();
  if (storagePref === 'session') return setSession();
  if (hasLocal) return setLocal();
  if (hasSession) return setSession();
  // fallback
  return setLocal();
};

const setRefreshToken = (token: string | null) => {
  if (typeof window === 'undefined') return;
  const storagePref = localStorage.getItem(TOKEN_STORAGE_KEY);
  const hasLocal = !!localStorage.getItem(REFRESH_TOKEN_KEY);
  const hasSession = !!sessionStorage.getItem(REFRESH_TOKEN_KEY);

  const setLocal = () => {
    if (token) localStorage.setItem(REFRESH_TOKEN_KEY, token);
    else localStorage.removeItem(REFRESH_TOKEN_KEY);
  };
  const setSession = () => {
    if (token) sessionStorage.setItem(REFRESH_TOKEN_KEY, token);
    else sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  };

  if (storagePref === 'local') return setLocal();
  if (storagePref === 'session') return setSession();
  if (hasLocal) return setLocal();
  if (hasSession) return setSession();
  return setLocal();
};

const setSessionExpiresAt = (iso: string | null) => {
  if (typeof window === 'undefined') return;
  if (iso) {
    localStorage.setItem(SESSION_INFO_KEY, iso);
    localStorage.setItem(SESSION_EXPLICIT_KEY, '1');
  } else {
    localStorage.removeItem(SESSION_INFO_KEY);
    localStorage.removeItem(SESSION_EXPLICIT_KEY);
  }
};

export const notifyAuthExpired = () => {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
  } catch {
    // ignore
  }
};

// Standard-Headers für alle Anfragen
const getDefaultHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  // Testsystem: das Backend rechnet für vorgespulte Standorte mit deren Datum.
  const standortId = aktiverStandort();
  if (standortId) {
    headers[STANDORT_HEADER] = String(standortId);
  }

  return headers;
};

/** Beendet die Sitzung lokal und meldet das der App. */
const endSession = () => {
  setAuthToken(null);
  setRefreshToken(null);
  setSessionExpiresAt(null);
  notifyAuthExpired();
};

const base64UrlDecode = (value: string): string => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  return atob(padded);
};

/**
 * Ist der Access-Token abgelaufen?
 *
 * Konservativ: alles, was sich nicht sicher als abgelaufen bestimmen laesst
 * (kein JWT, kaputtes Payload, kein `exp`), gilt als gueltig. Ein Irrtum in
 * diese Richtung kostet einen 401 mit anschliessendem Refresh — in die andere
 * Richtung wuerde er eine intakte Sitzung wegwerfen.
 */
export const isAccessTokenExpired = (token: string | null): boolean => {
  if (!token) return true;
  const parts = token.split('.');
  if (parts.length !== 3) return false;
  try {
    const payload = JSON.parse(base64UrlDecode(parts[1])) as { exp?: unknown };
    if (typeof payload.exp !== 'number') return false;
    // Puffer gegen Uhrendrift und die Laufzeit des Requests selbst.
    return payload.exp * 1000 <= Date.now() + 10_000;
  } catch {
    return false;
  }
};

/**
 * `unavailable` bedeutet: voruebergehend gescheitert (Netzwerk, 5xx, Proxy).
 * In dem Fall darf die Sitzung NICHT verworfen werden — sonst loggt ein
 * einzelner Aussetzer den Benutzer dauerhaft aus.
 */
type RefreshOutcome = 'refreshed' | 'no-token' | 'rejected' | 'unavailable';

let refreshInFlight: Promise<RefreshOutcome> | null = null;

async function refreshAccessToken(): Promise<RefreshOutcome> {
  if (typeof window === 'undefined') return 'unavailable';
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async (): Promise<RefreshOutcome> => {
    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) return 'no-token';
      const fullUrl = `${API_BASE_URL}/api/auth/refresh`;
      const res = await fetch(fullUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) {
        // Nur das Backend selbst kann den Refresh-Token endgueltig ablehnen.
        return res.status === 401 || res.status === 403 ? 'rejected' : 'unavailable';
      }
      const data = await res.json();
      const newToken = data?.token || data?.access_token;
      const newRefreshToken = data?.refresh_token;
      const sessionExpiresAt = data?.session_expires_at;
      if (!newToken) return 'unavailable';
      setAuthToken(newToken);
      if (newRefreshToken) setRefreshToken(newRefreshToken);
      if (typeof sessionExpiresAt === 'string' && sessionExpiresAt.trim() !== '') {
        setSessionExpiresAt(sessionExpiresAt);
      }
      return 'refreshed';
    } catch {
      return 'unavailable';
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

const extractErrorMessage = (responseData: unknown, status: number): string => {
  if (typeof responseData === 'string' && responseData.trim() !== '') {
    return responseData;
  }
  if (!responseData || typeof responseData !== 'object') {
    return `HTTP ${status}`;
  }

  const payload = responseData as Record<string, unknown>;
  if (typeof payload.message === 'string' && payload.message.trim() !== '') {
    return payload.message;
  }

  const detail = payload.detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const firstDetail = detail[0];
    if (firstDetail && typeof firstDetail === 'object') {
      const firstDetailRecord = firstDetail as Record<string, unknown>;
      if (typeof firstDetailRecord.msg === 'string' && firstDetailRecord.msg.trim() !== '') {
        return firstDetailRecord.msg;
      }
    }
  }

  const error = payload.error;
  if (typeof error === 'string' && error.trim() !== '') {
    return error;
  }
  if (error && typeof error === 'object') {
    const errorObj = error as Record<string, unknown>;
    const code = typeof errorObj.code === 'string' ? errorObj.code : null;
    const message = typeof errorObj.message === 'string' ? errorObj.message : null;
    if (message && code) return `${message} (${code})`;
    if (message) return message;
    if (code) return code;
  }

  return `HTTP ${status}`;
};

type RequestOnceFn = (
  requestUrl?: string,
  requestOptions?: RequestInit
) => Promise<{ response: Response; responseData: unknown }>;


// HTTP-Client mit Fehlerbehandlung
export const httpClient = async <T>(
  url: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  const requestOnce: RequestOnceFn = async (
    requestUrl: string = url,
    requestOptions: RequestInit = options
  ): Promise<{ response: Response; responseData: unknown }> => {
    const fullUrl = `${API_BASE_URL}${requestUrl}`;
    const headers = getDefaultHeaders();
    const response = await fetch(fullUrl, {
      ...requestOptions,
      credentials: 'include',
      headers: {
        ...headers,
        ...requestOptions.headers,
      },
    });
    if (response.ok && aktiverStandort()) {
      meldeSimuliertBis(response.headers.get(SIMULIERT_BIS_HEADER));
    }

    // Nur Beta-Testphase: fehlgeschlagene Aufrufe wandern in den Ringpuffer, damit
    // eine Fehlermeldung den Aufruf mitbringt, an dem es hing.
    if (!response.ok) {
      try {
        merkeFehlgeschlagenenRequest({
          method: (requestOptions.method || 'GET').toString(),
          url: requestUrl,
          status: response.status,
          requestId: response.headers.get('X-Request-Id'),
        });
      } catch {
        // Der Mitschnitt darf keinen Request scheitern lassen.
      }
    }

    const contentType = response.headers.get('content-type') || '';
    let responseData: unknown = undefined;
    if (response.status !== 204) {
      if (contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        const text = await response.text();
        responseData = text ? { message: text } : undefined;
      }
    }
    return { response, responseData };
  };

  try {
    const fullUrl = `${API_BASE_URL}${url}`;
    const method = (options.method || 'GET').toUpperCase();

    const { response, responseData } = await requestOnce();

    if (!response.ok) {
      // Auto-refresh once on 401 (except refresh endpoint itself)
      if (response.status === 401 && !fullUrl.endsWith('/api/auth/refresh')) {
        const outcome = await refreshAccessToken();

        if (outcome === 'refreshed') {
          const { response: retryResponse, responseData: retryData } = await requestOnce();
          if (retryResponse.ok) {
            const normalized = normalizeSuccessfulData(url, method, retryData);
            return { data: normalized as T | undefined };
          }

          const message = extractErrorMessage(retryData, retryResponse.status);
          // Frischer Token und trotzdem 401 — die Sitzung ist wirklich vorbei.
          if (retryResponse.status === 401) endSession();
          return { error: message as string };
        }

        // Refresh war voruebergehend nicht moeglich (Netzwerk, 5xx, Proxy):
        // Tokens behalten, damit der naechste Request es erneut versuchen kann.
        if (outcome === 'unavailable') {
          return { error: extractErrorMessage(responseData, response.status) as string };
        }

        // 'no-token' oder 'rejected' — hier ist die Sitzung tatsaechlich zu Ende.
        endSession();
        return { error: extractErrorMessage(responseData, response.status) as string };
      }

      const message = extractErrorMessage(responseData, response.status);
      // Betrifft jetzt nur noch den Refresh-Endpunkt selbst.
      if (response.status === 401) endSession();
      return { error: message as string };
    }

    const normalized = normalizeSuccessfulData(url, method, responseData);
    return { data: normalized as T | undefined };
  } catch (error) {
    console.error('API request failed:', error);
    return {
      error: error instanceof Error ? error.message : 'Netzwerkfehler',
    };
  }
};

// API-Client
export const api = {
  requireData: <T>(response: { data?: T; error?: string }, fallbackMessage: string): T => {
    if (response.error) throw new Error(response.error);
    if (response.data === undefined) throw new Error(fallbackMessage);
    return response.data;
  },
  get: async <T>(url: string, options?: ApiOptions): Promise<T> => {
    const params = options?.params ? `?${new URLSearchParams(options.params).toString()}` : '';
    const urlWithParams = `${url}${params}`;
    const response = await httpClient<T>(urlWithParams, {
      method: 'GET',
      headers: options?.headers,
    });
    return api.requireData(response, `GET ${urlWithParams} lieferte keine Daten`);
  },

  post: async <T>(url: string, data: unknown, options?: ApiOptions): Promise<T> => {
    const response = await httpClient<T>(url, {
      method: 'POST',
      headers: options?.headers,
      body: JSON.stringify(data),
    });
    return api.requireData(response, `POST ${url} lieferte keine Daten`);
  },

  put: async <T>(url: string, data: unknown, options?: ApiOptions): Promise<T> => {
    const response = await httpClient<T>(url, {
      method: 'PUT',
      headers: options?.headers,
      body: JSON.stringify(data),
    });
    return api.requireData(response, `PUT ${url} lieferte keine Daten`);
  },

  patch: async <T>(url: string, data: unknown, options?: ApiOptions): Promise<T> => {
    const response = await httpClient<T>(url, {
      method: 'PATCH',
      headers: options?.headers,
      body: JSON.stringify(data),
    });
    return api.requireData(response, `PATCH ${url} lieferte keine Daten`);
  },

  delete: async <T>(url: string, options?: ApiOptions): Promise<T> => {
    const response = await httpClient<T>(url, {
      method: 'DELETE',
      headers: options?.headers,
    });
    if (response.error) {
      throw new Error(response.error);
    }
    // FastAPI commonly answers successful DELETE requests with 204 No Content.
    return response.data as T;
  },

  // Hilfsmethode für Datei-Uploads
  upload: async <T>(url: string, formData: FormData): Promise<T> => {
    const token = getAuthToken();
    const headers: Record<string, string> = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(extractErrorMessage(errorData, response.status));
    }

    return response.json();
  },
};

export { API_BASE_URL };
